import { Box, Group, UnstyledButton } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { getSpacing, spacing } from "#app/theme/v2/spacing";
import { Badge } from "#components/atoms/Badge/Badge";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { Loader } from "#components/atoms/Loader/Loader";
import { Tooltip } from "#components/atoms/Tooltip/Tooltip";
import { Text } from "#components/atoms/Typographies/Text";
import { formatPartDimensions } from "#modules/parts/domain/formatPartDimensions";
import { getCanDisplayReliableChip } from "#modules/quoteRequestLines/domain/getCanDisplayReliableChip";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { SimilarPartCell } from "#modules/quoteRequestLinesV2/view/organisms/SimilarPartCell";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import { ThumbnailOverlay } from "#shared/view/components/Thumbnails/ThumbnailOverlay";
import { formatPrice, PartTypeSchema } from "shared";

type Props = {
  line: QuoteRequestLineDto;
  depth?: number;
  isAssemblyParent?: boolean;
  childrenCount?: number;
  isAssemblyAnalyzing?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  unitPrice?: number | null;
  onEdit?: () => void;
};

const SIMILAR_PART_SLOT_WIDTH = 52;

const AssemblyChildConnector = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="25" viewBox="0 0 13 25" fill="none">
    <path d="M0.5 0V16C0.5 20.4183 4.08172 24 8.5 24H12.5" stroke={colors.icon.tertiary} />
  </svg>
);

export const PartCell = ({
  line,
  depth = 0,
  isAssemblyParent = false,
  childrenCount = 0,
  isAssemblyAnalyzing = false,
  isCollapsed = false,
  onToggleCollapse,
  unitPrice = null,
  onEdit,
}: Props) => {
  const { t } = useTranslation();

  const isConsumable = line.part.type === PartTypeSchema.enum.CONSUMABLE;

  const typeLabel = line.part.type ? t(`part.type.${line.part.type}`) : null;
  const dimensions = formatPartDimensions(line.part);
  const subtitle = isConsumable
    ? t("quoteRequestV2.column.consumableSubtitle", { price: formatPrice(unitPrice) })
    : [typeLabel, dimensions].filter(Boolean).join(" · ");

  const canQueryReliableChip = getCanDisplayReliableChip({
    isFetchedQuoteRequestLine: true,
    isAssemblyParent,
    part: line.part,
  });

  return (
    <Group gap={spacing[3]} wrap="nowrap">
      {depth > 0 && (
        <div
          style={{
            flexShrink: 0,
            marginLeft: getSpacing(5) * depth,
            marginTop: getSpacing(0.5),
            alignSelf: "start",
          }}
        >
          <AssemblyChildConnector />
        </div>
      )}
      <Group gap={spacing[4]} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
        <ThumbnailOverlay
          part={line.part}
          quoteRequestLineId={line.id}
          size={{ width: 48, height: 48 }}
        />
        <Group gap={spacing[2]} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Flex direction="column" gap={spacing[1]} style={{ flex: 1, minWidth: 0 }}>
            <Group gap={spacing[1.5]} wrap="nowrap">
              {isAssemblyParent && (
                <UnstyledButton
                  onClick={onToggleCollapse}
                  aria-label={
                    isCollapsed
                      ? t("quoteRequestV2.assembly.expand")
                      : t("quoteRequestV2.assembly.collapse")
                  }
                  aria-expanded={!isCollapsed}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    flexShrink: 0,
                    backgroundColor: colors.bg.button.secondary.default,
                    borderRadius: radius.md,
                    padding: spacing[1],
                  }}
                >
                  {isCollapsed ? (
                    <Icon name="chevronRight" size={12} color={colors.icon.primaryDefault} />
                  ) : (
                    <Icon name="chevronDown" size={12} color={colors.icon.primaryDefault} />
                  )}
                </UnstyledButton>
              )}
              <Group gap={spacing[1]} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                <Text size="small" type="medium" truncate style={{ flex: 1, minWidth: 0 }}>
                  {line.part.name}
                </Text>
              </Group>
            </Group>
            {isAssemblyParent ? (
              <Group gap={spacing[1.5]} wrap="nowrap">
                {isAssemblyAnalyzing ? (
                  <Box
                    bg={colors.bg.status.alternative.tertiary}
                    px={spacing[2.5]}
                    py={spacing[1]}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      borderRadius: radius.xxs,
                    }}
                    data-testid="assembly-analyzing"
                  >
                    <Loader size="small" />
                  </Box>
                ) : (
                  <Badge variant="alternative" shape="squared">
                    {t("quoteRequestV2.assembly.badge", { count: childrenCount })}
                  </Badge>
                )}
                {dimensions && (
                  <Text size="tiny" type="regular" color={colors.text.secondaryDefault} truncate>
                    {dimensions}
                  </Text>
                )}
              </Group>
            ) : (
              <Text size="tiny" type="regular" color={colors.text.secondaryDefault} truncate>
                {subtitle}
              </Text>
            )}
          </Flex>
          {onEdit && (
            <Box data-reveal-on-row-hover style={{ flexShrink: 0 }}>
              <Tooltip label={t("quoteRequestV2.editPartModal.title")} position="bottom">
                <IconButton
                  variant="custom"
                  size="sm"
                  aria-label={t("quoteRequestV2.editPartModal.title")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                >
                  <Icon name="pencil" size={16} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Box style={{ flexShrink: 0, width: SIMILAR_PART_SLOT_WIDTH }}>
            {canQueryReliableChip && <SimilarPartCell line={line} />}
          </Box>
        </Group>
      </Group>
    </Group>
  );
};
